import React from "react";
import { cn } from "@/lib/utils";
import {
  ColumnDef,
  flexRender,
  Table as TableType,
} from "@tanstack/react-table";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  ChevronsUpDownIcon,
} from "lucide-react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { TableVariantT, tableVariants } from "@/components/ui/table";
import QuickTooltip from "./quick-tooltip";

interface ThaloDataTableInfiniteProps<TData> {
  table: TableType<TData>;
  columns: ColumnDef<TData>[];
  variant?: TableVariantT;
  hasNextPage?: boolean;
  fetchNextPage?: () => void;
  isFetchingNextPage?: boolean;
  isLoading?: boolean;
  onRowClick?: (row: any) => void;
  onMouseEnter?: (row: any) => void;
  onMouseLeave?: (row: any) => void;
  rowHightlightClass?: (row: any) => string;
}

export function ThaloDataTableInfinite<TData>({
                                                table,
                                                columns,
                                                variant,
                                                hasNextPage = false,
                                                fetchNextPage,
                                                isFetchingNextPage = false,
                                                isLoading = false,
                                                onRowClick,
                                                onMouseEnter,
                                                onMouseLeave,
                                                rowHightlightClass,
                                              }: ThaloDataTableInfiniteProps<TData>) {
  const tableContainerRef = React.useRef<HTMLDivElement>(null);
  const { rows } = table.getRowModel();

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => 53,
    overscan: 5,
  });

  React.useEffect(() => {
    if (!fetchNextPage) return;

    const [lastItem] = [...rowVirtualizer.getVirtualItems()].reverse();

    if (!lastItem) return;

    if (
      lastItem.index >= rows.length - 1 &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
  }, [
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    rowVirtualizer.getVirtualItems(),
    rows.length,
  ]);

  return (
    <div
      ref={tableContainerRef}
      className="rounded-md overflow-auto relative"
      style={{ height: '500px' }}
    >
      <table style={{ display: 'grid' }} className={cn(tableVariants({ variant }))}>
        <thead
          style={{
            display: 'grid',
            position: 'sticky',
            top: 0,
            zIndex: 1,
          }}
          className="bg-background"
        >
        {table.getHeaderGroups().map((headerGroup) => (
          <tr
            key={headerGroup.id}
            style={{ display: 'flex', width: '100%' }}
            className="border-b"
          >
            {headerGroup.headers.map((header) => (
              <th
                key={header.id}
                style={{
                  display: 'flex',
                  width: header.getSize(),
                }}
                className={cn(
                  "h-12 px-4 text-left align-middle font-medium text-muted-foreground bg-background",
                  "text-xs lg:text-sm",
                  header.column.getCanSort() ? "cursor-pointer" : "",
                  header.column.columnDef.meta?.className
                )}
                onClick={header.column.getCanSort() ? header.column.getToggleSortingHandler() : undefined}
              >
                <div className="w-full flex flex-nowrap gap-x-2 h-full items-center">
                  {header.column.columnDef.meta?.tooltip ? (
                    <QuickTooltip text={header.column.columnDef.meta.tooltip}>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </QuickTooltip>
                  ) : (
                    flexRender(header.column.columnDef.header, header.getContext())
                  )}
                  {header.column.getIsSorted() === "asc" ? (
                    <ChevronUpIcon className="h-3 w-3 lg:h-4 lg:w-4" />
                  ) : header.column.getIsSorted() === "desc" ? (
                    <ChevronDownIcon className="h-3 w-3 lg:h-4 lg:w-4" />
                  ) : header.column.getCanSort() ? (
                    <ChevronsUpDownIcon className="h-3 w-3 lg:h-4 lg:w-4" />
                  ) : null}
                </div>
              </th>
            ))}
          </tr>
        ))}
        </thead>
        <tbody
          style={{
            display: 'grid',
            height: `${rowVirtualizer.getTotalSize()}px`,
            position: 'relative',
          }}
        >
        {rowVirtualizer.getVirtualItems().length > 0 ? (
          rowVirtualizer.getVirtualItems().map((virtualRow) => {
            const row = rows[virtualRow.index];
            return (
              <tr
                key={row.id}
                data-index={virtualRow.index}
                ref={(node) => rowVirtualizer.measureElement(node)}
                style={{
                  display: 'flex',
                  position: 'absolute',
                  transform: `translateY(${virtualRow.start}px)`,
                  width: '100%',
                }}
                className={cn(
                  "border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted",
                  "text-xs lg:text-sm",
                  onRowClick ? "cursor-pointer" : "",
                  rowHightlightClass?.(row)
                )}
                data-state={row.getIsSelected() ? "selected" : undefined}
                onClick={() => onRowClick?.(row)}
                onMouseEnter={() => onMouseEnter?.(row)}
                onMouseLeave={() => onMouseLeave?.(row)}
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    style={{
                      display: 'flex',
                      width: cell.column.getSize(),
                    }}
                    className={cn(
                      "p-4 align-middle",
                      cell.column.columnDef.meta?.className
                    )}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            );
          })
        ) : (
          <tr style={{ display: 'flex', width: '100%' }}>
            <td className="h-24 text-center p-4" style={{ width: '100%' }}>
              {isLoading ? "Loading..." : "No results."}
            </td>
          </tr>
        )}
        {isFetchingNextPage && (
          <tr style={{ display: 'flex', width: '100%', position: 'absolute', bottom: 0 }}>
            <td className="h-12 text-center p-4" style={{ width: '100%' }}>
              Loading more...
            </td>
          </tr>
        )}
        </tbody>
      </table>
    </div>
  );
}